import { Check } from "lucide-react";
import { PERMISSION_IMPLIED_BY, type OrganizationType, type PermissionKey } from "@bookdental/schemas";
import { gruposPorEscopo } from "@/components/permissions/permission-scopes";
import { cn } from "@/lib/cn";

type Props = {
  tipos: OrganizationType[];
  efetiva: (k: PermissionKey) => boolean;
  mexidas: Partial<Record<PermissionKey, boolean>>;
  onAlternar: (k: PermissionKey) => void;
  editavel: boolean;
};

/** As permissões de um acesso, agrupadas por área como na matriz de Configurações. */
export function PermissoesDoAcesso({ tipos, efetiva, mexidas, onAlternar, editavel }: Props) {
  return (
    <div className="space-y-3">
      {gruposPorEscopo(tipos).map((escopo) => (
        <div key={escopo.scope}>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/70">
            {escopo.label}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {escopo.groups.map((grupo) => (
              <div key={grupo.resource} className="rounded-lg border border-zinc-100 p-2.5 dark:border-border">
                <p className="mb-1.5 text-[12px] font-semibold text-zinc-800 dark:text-foreground">{grupo.label}</p>
                <div className="space-y-1">
                  {grupo.keys.map(({ key, label }) => {
                    const quemImplica = PERMISSION_IMPLIED_BY[key];
                    const implicada = !!quemImplica && efetiva(quemImplica);
                    const marcada = implicada || efetiva(key);
                    const travada = !editavel || implicada;
                    return (
                      <button
                        key={key}
                        type="button"
                        role="switch"
                        aria-checked={marcada}
                        disabled={travada}
                        onClick={() => onAlternar(key)}
                        title={implicada ? "Incluído em Gerenciar" : undefined}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12px] transition-colors",
                          !travada && "hover:bg-zinc-50 dark:hover:bg-muted",
                          travada && "cursor-default",
                          key in mexidas && "bg-amber-50 dark:bg-amber-500/10",
                        )}
                      >
                        <span
                          className={cn(
                            "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                            marcada
                              ? "border-brand-600 bg-brand-600 text-white dark:border-primary dark:bg-primary"
                              : "border-zinc-300 bg-white dark:border-border dark:bg-card",
                            implicada && "opacity-60",
                          )}
                        >
                          {marcada && <Check className="h-3 w-3" />}
                        </span>
                        <span className="text-zinc-700 dark:text-foreground/90">{label}</span>
                        {implicada && (
                          <span className="ml-auto text-[10px] text-zinc-400 dark:text-muted-foreground">
                            incluído
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="text-[11px] text-zinc-400 dark:text-muted-foreground/70">
        Ver preços e transferir a propriedade são sempre exclusivos do proprietário.
      </p>
    </div>
  );
}
